import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getEmailField, getTextField } from '@lender/utils';
import { Button, Typography } from '@mui/material';
import { EmailIcon, KeyIcon } from '@sharedCR/coreDesignSystem/icons';
import { getBiometryConsentInfo } from 'app/services/biometricGate';
import { getKeepMeSignedIn, setKeepMeSignedIn } from 'app/services/secureTokenStore';
import InputAdornment from 'app/shared/components/common/InputAdornment';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import { getIsNativePlatform } from 'utils/platform';

import {
  requestOtpSignInAction,
  resetLoginErrorAction,
  submitLoginAction,
  submitPasswordSignInAction,
} from 'store/auth/actions';
import { openConfirmationDialogAction } from 'store/global/actions';

import { BACK_TO_PASSWORD_LABEL } from '../Login.constants';
import { LoginTestIds } from '../Login.testIds';

import {
  EMAIL_CODE_LINK_LABEL,
  getKeepSignedInPrompt,
  KEEP_SIGNED_IN_ACCEPT_LABEL,
  KEEP_SIGNED_IN_DECLINE_LABEL,
  PASSWORD_INPUT_PROPS,
  USERNAME_INPUT_PROPS,
} from './JWTLoginTab.constants';
import { getLoginErrorMessage, upgradeToTrustedDevice } from './JWTLoginTab.utils';

const passwordSchema = object().shape({
  email: getEmailField(),
  password: getTextField(),
});

const emailOnlySchema = object().shape({
  email: getEmailField(),
});

const InputLabelProps = { shrink: true };

const JWTLoginTab = ({
  onSecondFactorRequired,
  onEmailCodeRequested,
  notice,
  isOtpOnly: isOtpOnlyProp,
}) => {
  const dispatch = useDispatch();
  const login = useSelector(({ auth }) => auth.login);
  const [isEmailCodeMode, setIsEmailCodeMode] = useState(false);

  const hasError = !!login?.error;
  const isOtpOnly = isOtpOnlyProp || isEmailCodeMode;
  const onCodeRequested = isEmailCodeMode ? onEmailCodeRequested : onSecondFactorRequired;
  const canRequestEmailCode = !isOtpOnlyProp && !!onEmailCodeRequested;
  const errorMessage = getLoginErrorMessage(login?.error);

  const { form, isFormChanged, validationErrors, setSchema, handleChange, validateForm } = useForm({
    email: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
  });

  const onChange = useCallback(
    e => {
      if (hasError) {
        dispatch(resetLoginErrorAction());
      }

      handleChange(e);
    },
    [dispatch, hasError, handleChange],
  );

  const promptKeepSignedIn = useCallback(
    displayName => {
      const { message, description } = getKeepSignedInPrompt(displayName);

      dispatch(
        openConfirmationDialogAction({
          message,
          description,
          onAccept: () => {
            setKeepMeSignedIn(true);
            upgradeToTrustedDevice();
          },
          onAcceptButtonName: KEEP_SIGNED_IN_ACCEPT_LABEL,
          onCancel: () => setKeepMeSignedIn(false),
          onCancelButtonName: KEEP_SIGNED_IN_DECLINE_LABEL,
        }),
      );
    },
    [dispatch],
  );

  const onEmailCodeModeToggle = useCallback(() => {
    if (hasError) {
      dispatch(resetLoginErrorAction());
    }

    setIsEmailCodeMode(prev => !prev);
  }, [dispatch, hasError]);

  useEffect(() => {
    const schema = isOtpOnly ? emailOnlySchema : passwordSchema;

    setSchema(schema);
  }, [setSchema, isOtpOnly]);

  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
  const handleSubmit = async e => {
    e.preventDefault();

    const copiedForm = { ...form, email: form.email.trim(), password: form.password.trim() };

    if (!(await validateForm({ newPassword: form.newPassword }))) {
      return;
    }

    delete copiedForm.newPassword;
    delete copiedForm.confirmPassword;

    if (isOtpOnly) {
      const requestId = await dispatch(requestOtpSignInAction({ email: copiedForm.email }));

      if (requestId && onCodeRequested) {
        onCodeRequested(requestId);
      }

      return;
    }

    if (onSecondFactorRequired) {
      const result = await dispatch(
        submitPasswordSignInAction({ email: copiedForm.email, password: copiedForm.password }),
      );

      if (result?.secondFactorRequired) {
        onSecondFactorRequired(result.signInRequestId);

        return;
      }

      const isLoginSuccess = !!result?.uuid;

      if (!isLoginSuccess || !getIsNativePlatform() || getKeepMeSignedIn()) {
        return;
      }

      const { isAvailable, displayName } = await getBiometryConsentInfo();

      if (!isAvailable || !displayName) {
        return;
      }

      promptKeepSignedIn(displayName);

      return;
    }

    const master = await dispatch(submitLoginAction(copiedForm));

    const isLoginSuccess = !!master?.uuid;

    if (!isLoginSuccess || !getIsNativePlatform() || getKeepMeSignedIn()) {
      return;
    }

    const { isAvailable, displayName } = await getBiometryConsentInfo();

    if (!isAvailable || !displayName) {
      return;
    }

    promptKeepSignedIn(displayName);
  };

  return (
    <div className="w-full">
      <form
        noValidate
        className="flex flex-col justify-center w-full"
        data-testid={LoginTestIds.JWT_FORM}
        onSubmit={handleSubmit}
      >
        <TextFieldCDS
          required
          withStaticHelper
          InputLabelProps={InputLabelProps}
          className="mb-16"
          dataTestId={LoginTestIds.JWT_USERNAME_INPUT}
          endAdornment={<InputAdornment adornmentIcon={EmailIcon} />}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          inputProps={USERNAME_INPUT_PROPS}
          label="Username/Email"
          name="email"
          type="text"
          value={form.email}
          variant="outlined"
          onChange={onChange}
        />

        {!isOtpOnly && (
          <TextFieldCDS
            required
            withStaticHelper
            InputLabelProps={InputLabelProps}
            className="mb-16"
            dataTestId={LoginTestIds.JWT_PASSWORD_INPUT}
            endAdornment={<InputAdornment adornmentIcon={KeyIcon} />}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            inputProps={PASSWORD_INPUT_PROPS}
            label="Password"
            name="password"
            type="password"
            value={form.password}
            variant="outlined"
            onChange={onChange}
          />
        )}

        {notice && !errorMessage && (
          <Typography
            className="py-8 text-center"
            color="text.secondary"
            data-testid={LoginTestIds.JWT_NOTICE_MESSAGE}
            role="status"
            variant="medium14"
          >
            {notice}
          </Typography>
        )}

        {errorMessage && (
          <div className="flex flex-col items-center text-red" role="alert">
            <Typography
              className="py-8"
              data-testid={LoginTestIds.JWT_ERROR_MESSAGE}
              variant="medium14"
            >
              {errorMessage}
            </Typography>
          </div>
        )}
        <Button
          aria-label="LOG IN"
          className="w-full mx-auto mt-16 normal-case"
          color="primary"
          data-testid={LoginTestIds.LOGIN_BUTTON}
          disabled={!isFormChanged || login.loading || hasError}
          type="submit"
          value="legacy"
          variant="contained"
        >
          {isOtpOnly ? 'Send Code' : 'Login'}
        </Button>

        {canRequestEmailCode && (
          <Button
            className="w-full mx-auto mt-8 normal-case"
            color="primary"
            data-testid={
              isEmailCodeMode ? LoginTestIds.JWT_BACK_TO_PASSWORD : LoginTestIds.JWT_EMAIL_CODE_LINK
            }
            type="button"
            variant="text"
            onClick={onEmailCodeModeToggle}
          >
            {isEmailCodeMode ? BACK_TO_PASSWORD_LABEL : EMAIL_CODE_LINK_LABEL}
          </Button>
        )}
      </form>
    </div>
  );
};

JWTLoginTab.propTypes = {
  isOtpOnly: PropTypes.bool,
  notice: PropTypes.string,
  onEmailCodeRequested: PropTypes.func,
  onSecondFactorRequired: PropTypes.func,
};

export default JWTLoginTab;
